import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ClipboardList } from "lucide-react";
import SEO from "../components/SEO";
import { CALENDLY_URL, ALIGNMENT_CHECK_URL } from "../lib/site";

/* -------------------------------------------------------------------------
 *  Insight 003 — Activity Isn't Progress.
 *  Activity and progress are not the same thing. One is movement; the other
 *  is meaningful improvement.
 *  Written under the Quiet Confidence Principle™.
 * ------------------------------------------------------------------------- */

export default function InsightActivityProgress() {
  return (
    <>
      <SEO
        title="Activity Isn't Progress | AlignED Insights"
        description="Schools can become exceptionally good at staying busy without becoming consistently better. Activity is movement. Progress is meaningful improvement. Insight 003 from the AlignED library."
      />

      {/* Article Hero */}
      <section className="bg-[#FDFBF7] border-b border-[#0A192F]/10" data-testid="insight-hero">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 pt-20 pb-12 lg:pt-24 lg:pb-16">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#52796F] hover:text-[#0A192F] transition-colors"
            data-testid="insight-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Insights
          </Link>
          <div className="mt-8 text-xs uppercase tracking-[0.25em] text-[#38BDF8]">
            Insight 003 &middot; Organizational Learning
          </div>
          <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] text-[#0A192F]">
            Activity Isn&apos;t Progress.
          </h1>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
            Schools can become exceptionally good at staying busy without becoming consistently better. Activity is movement. Progress is meaningful improvement.
          </p>
        </div>
      </section>

      {/* Article Body */}
      <article className="bg-[#FDFBF7]" data-testid="insight-body">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 pb-24">
          <div className="prose-editorial text-[#1A1D20] leading-[1.75] text-lg space-y-7">

            <p>
              Schools are busy places. Every day is filled with meetings, emails, phone calls, classroom visits, interventions, planning sessions, and urgent decisions.
            </p>

            <p>
              From the outside, it can appear that a great deal is happening. Often, it is.
            </p>

            <p>
              But activity and progress are not the same thing. One is movement. The other is meaningful improvement.
            </p>

            <p className="font-serif-display text-2xl sm:text-3xl text-[#0A192F] leading-snug border-l-2 border-[#38BDF8] pl-6 py-1">
              The distinction matters. Because organizations can become exceptionally good at staying busy without becoming consistently better.
            </p>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              The illusion of motion
            </h2>

            <p>
              Most educational leaders can describe a day when everyone worked incredibly hard. Conversations happened. Meetings were held. Students were supported. Families were contacted. Problems were addressed.
            </p>

            <p>
              Yet at the end of the week, an uncomfortable question remains:
            </p>

            <p className="font-serif-display text-2xl text-[#0A192F] leading-snug border-l-2 border-[#38BDF8] pl-6 py-1">
              What actually changed?
            </p>

            <p>
              Effort is essential. But effort alone does not guarantee improvement. Without reflection, visibility, and follow-through, activity can create the feeling of progress without producing lasting results.
            </p>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              Progress leaves evidence
            </h2>

            <p>
              Real progress is visible. Not because every problem disappears. But because something has become more consistent than it was before.
            </p>

            <ul className="space-y-3 pl-6 border-l-2 border-[#D4AF37]">
              <li className="text-[#1A1D20]">Attendance interventions happen earlier.</li>
              <li className="text-[#1A1D20]">Behavior expectations become more predictable.</li>
              <li className="text-[#1A1D20]">Family communication becomes more dependable.</li>
              <li className="text-[#1A1D20]">Leadership conversations become more focused.</li>
              <li className="text-[#1A1D20]">Successful practices begin spreading from one team to another.</li>
            </ul>

            <p>
              Progress changes the way an organization operates. Not just how busy it feels.
            </p>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              The leadership trap
            </h2>

            <p>
              Many organizations unintentionally reward activity. Quick responses. Full calendars. Frequent meetings. Constant communication.
            </p>

            <p>
              These are often signs of commitment. But they are not reliable measures of improvement.
            </p>

            <p>
              Leadership becomes more effective when it asks different questions.
            </p>

            <div className="space-y-6 pt-2">
              <div className="border-l-2 border-[#0A192F]/15 pl-6">
                <p className="text-[#4A5568]">Instead of:</p>
                <p className="mt-1 font-serif-display text-xl text-[#4A5568] italic">&ldquo;How much did we do?&rdquo;</p>
                <p className="mt-3 text-[#4A5568]">Ask:</p>
                <p className="mt-1 font-serif-display text-xl text-[#0A192F]">&ldquo;What became more consistent because of our work?&rdquo;</p>
              </div>
              <div className="border-l-2 border-[#0A192F]/15 pl-6">
                <p className="text-[#4A5568]">Instead of:</p>
                <p className="mt-1 font-serif-display text-xl text-[#4A5568] italic">&ldquo;How many meetings did we hold?&rdquo;</p>
                <p className="mt-3 text-[#4A5568]">Ask:</p>
                <p className="mt-1 font-serif-display text-xl text-[#0A192F]">&ldquo;What decisions became clearer?&rdquo;</p>
              </div>
              <div className="border-l-2 border-[#0A192F]/15 pl-6">
                <p className="text-[#4A5568]">Instead of:</p>
                <p className="mt-1 font-serif-display text-xl text-[#4A5568] italic">&ldquo;Did we complete the task?&rdquo;</p>
                <p className="mt-3 text-[#4A5568]">Ask:</p>
                <p className="mt-1 font-serif-display text-xl text-[#0A192F]">&ldquo;Did the system become stronger?&rdquo;</p>
              </div>
            </div>

            <p>
              Those questions shift attention from effort to impact.
            </p>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              The execution connection
            </h2>

            <p>
              This is where the Execution Gap&trade; becomes visible.
            </p>

            <p>
              Organizations rarely struggle because people are inactive. They struggle because important work is not consistently translated into everyday practice.
            </p>

            <p>
              Teams stay busy. Priorities remain clear. Yet execution still varies.
            </p>

            <p>
              The goal is not to create more activity. The goal is to reduce unnecessary activity by strengthening the systems that make important work happen more consistently.
            </p>

            <p className="font-serif-display text-xl sm:text-2xl text-[#52796F] leading-snug pt-2">
              Strong systems create focus. Focus creates progress.
            </p>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              What progress feels like
            </h2>

            <p>
              Progress often feels quieter than activity.
            </p>

            <ul className="space-y-3 pl-6 border-l-2 border-[#D4AF37]">
              <li className="text-[#1A1D20]">There are fewer reminders because ownership is clearer.</li>
              <li className="text-[#1A1D20]">There are fewer surprises because visibility has improved.</li>
              <li className="text-[#1A1D20]">There are fewer repeated conversations because learning has been captured.</li>
              <li className="text-[#1A1D20]">There is less urgency because important work is no longer depending on memory alone.</li>
            </ul>

            <p>
              The organization feels calmer. Not because people care less. Because the system carries more of the work.
            </p>

            <p className="font-serif-display text-xl text-[#0A192F] leading-snug">
              That is one of the quiet signs of organizational maturity.
            </p>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              A better leadership question
            </h2>

            <p>Instead of asking:</p>

            <p className="font-serif-display text-2xl text-[#0A192F] leading-snug border-l-2 border-[#38BDF8] pl-6 py-1">
              &ldquo;How busy are we?&rdquo;
            </p>

            <p>Ask:</p>

            <p className="font-serif-display text-2xl text-[#0A192F] leading-snug border-l-2 border-[#38BDF8] pl-6 py-1">
              &ldquo;What has become easier to do consistently?&rdquo;
            </p>

            <p>
              That question changes what leaders notice. It shifts attention toward patterns instead of events. Toward systems instead of effort. Toward improvement instead of motion.
            </p>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              Closing reflection
            </h2>

            <p>
              Activity can fill a calendar. Progress changes an organization.
            </p>

            <p>
              The strongest leadership teams do not simply create more movement. They create better systems.
            </p>

            <div className="pt-2 space-y-2">
              <p className="font-serif-display text-2xl text-[#0A192F] leading-snug">Better systems reduce unnecessary effort.</p>
              <p className="font-serif-display text-2xl text-[#0A192F] leading-snug">Better systems create more consistent execution.</p>
              <p className="font-serif-display text-2xl text-[#52796F] leading-snug">And more consistent execution creates better outcomes for students, families, and educators.</p>
            </div>

            <div className="pt-4 space-y-2">
              <p className="font-serif-display text-2xl text-[#0A192F] leading-snug">
                That is the difference between being busy&hellip;
              </p>
              <p className="font-serif-display text-2xl text-[#52796F] leading-snug">
                &hellip;and becoming better.
              </p>
            </div>
          </div>

          {/* Contextual Invitation */}
          <div className="mt-20 pt-10 border-t border-[#0A192F]/10">
            <p className="text-base text-[#4A5568] leading-relaxed">
              If this perspective resonates with your leadership team, take the Alignment Check&trade; to identify where activity may be masking opportunities for more consistent execution.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href={ALIGNMENT_CHECK_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
                data-testid="insight-cta-ac"
              >
                <ClipboardList className="w-4 h-4" strokeWidth={1.5} /> Take the Alignment Check&trade;
              </a>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex justify-center items-center gap-2 text-[#0A192F] text-sm tracking-wide border-b border-[#0A192F] pb-1 self-start sm:self-center hover:text-[#52796F] hover:border-[#52796F] transition-colors"
                data-testid="insight-cta-walkthrough"
              >
                Schedule a Walkthrough <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div className="mt-16 flex justify-center">
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 text-sm text-[#52796F] hover:text-[#0A192F] transition-colors border-b border-[#52796F]/40 hover:border-[#0A192F] pb-1"
              data-testid="insight-return-link"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Return to Insights
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}
